import qs from 'qs'
import axios, {
  AxiosError,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios'
import merge from 'lodash.merge'
import { Get } from './Get'
import { Put } from './Put'
import { Post } from './Post'
// import {env} from '../Application';
import { UserPreferencesData, RegisterUserData } from './UserService.d'
import { store } from '~/Store'
import { UserSelectors } from '~/Store/User'
import { Delete } from './Delete'

interface LoginResponse {
  access_token: string
  expires_in: number
  token_type: string
  refresh_token?: string
  scope: string
}

export interface ListOrganizationResponse {
  guid: string
  companyName: string
  billingAddress: string
  billingAddress2: string
  billingCity: string
  billingState: string
  billingZip: string
  crmId: string | null
  duns: string
  naics: string | null
  userOrganization: string | null
  licenses: string | null
  organizationRoles: string | null
}

export interface User {
  id: string
  invitationStatus: string
  userName: string
  email: string
  phoneNumber: string | null
  firstname: string
  lastname: string
  avatarUrl: string | null
  forcePasswordChange: boolean
  profile: string | null
}

export interface LicenseType {
  guid: string
  offlineRange: number
  type: string
  name: string
}

export interface License {
  users: User[]
  number: number
  guid: string
  state: string | null
  licenseType: LicenseType
  organizationId: string
  userGuid: string | null
  expiredate: string
  licenseTypeId: string
  activeDate: string
  limitDevices: number
}

export interface dateLicense {
  expiredate: string
  licenseTypeId: string
  activeDate: string
  limitDevices: number
  number: number
}

export interface User {
  userOrgId: string
  organizationRoles: string[]
  id: string
  invitationStatus: string
  userName: string
  email: string
  phoneNumber: string | null
  firstname: string
  lastname: string
  avatarUrl: string | null
  forcePasswordChange: boolean
  profile: string | null
}

export interface UserListResponse {
  users: User[]
}

export interface NewLicenseData {
  expiredate: string
  licenseTypeId: string
  activeDate: string
  limitDevices: number
  number: number
}
class UserServiceController {
  private licenseManagerUrl =
    process.env.LICENSE_MANAGER_URL ??
    window.location.protocol +
      '//' +
      window.location.hostname +
      '/license_manager'

  private identityServerUrl =
    process.env.IDENTITY_SERVER_URL ??
    window.location.protocol + '//' + window.location.hostname;

  public Put = {
    changePassword: (data: { oldPassword: string; newPassword: string }) =>
      Put(`${this.licenseManagerUrl}/users/current/password`, data),

    confirmUserEmail: (cb: string) => {
      const url = decodeURI(cb)
      return Put(url)
    },
    forgotPassword: (cb: string, password: string) => {
      let url = decodeURI(cb)
      if (url.indexOf(location.protocol) == -1) {
        if (
          url.indexOf('https') == -1 &&
          location.protocol.indexOf('https') != -1
        ) {
          url = url.replace('http', 'https')
        }
      }
      const formData = new FormData()
      formData.append('password', password)
      return axios.put(url, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
    },
    confirmInvitation: (orgId: string, accept: boolean) =>
      Put(
        `${this.licenseManagerUrl}/users/current/organizations/${orgId}/invitationStatus?accept=${accept}`,
      ),
    updateUserProfile: (data: any) =>
      Put(`${this.licenseManagerUrl}/users/current`, data),
    updateDateLicense: (
      orgId: number,
      input: dateLicense,
      options: AxiosRequestConfig = {},
    ) =>
      Put(
        `${this.licenseManagerUrl}/organizations/${orgId}/licenses`,
        input,
        options,
      ),
  }

  public Get = {
    fetchCurrentUserAvatar: (options: AxiosRequestConfig = {}) =>
      Get(`${this.licenseManagerUrl}/users/current/avatar`, options),
    fetchCurrentUser: (options: AxiosRequestConfig = {}) => {
      // const state = store.getState()
      // const userEmail = UserSelectors.getEmail(state)
      // return Get(`${this.licenseManagerUrl}/users/${userEmail}`, options)
      return Get(`${this.licenseManagerUrl}/users/current/profile`, options)
    },
    fetchCurrentUserOrganizations: (options: AxiosRequestConfig = {}) =>
      Get(`${this.licenseManagerUrl}/users/current/organizations`, options),
    fetchCurrentUserLicenses: (options: AxiosRequestConfig = {}) =>
      Get(`${this.licenseManagerUrl}/users/current/licenses`, options),
    fetchCurrentUserPreferences: (options: AxiosRequestConfig = {}) =>
      Get(
        `${this.licenseManagerUrl}/users/current/preferences/currentClient`,
        options,
      ),
    fetchUserByEmail: (email: string, options: AxiosRequestConfig = {}) =>
      Get(`${this.licenseManagerUrl}/users/${email}`, options),
    fetchUserLicenses: (userId: string, options: AxiosRequestConfig = {}) =>
      Get(`${this.licenseManagerUrl}/users/${userId}/licenses`, options),
    fetchTokenForPasswordReset: (options: AxiosRequestConfig = {}) =>
      Get(`${this.licenseManagerUrl}/users/current/password/token`, options),
    getCaptchaByEmail: (email: string, options: AxiosRequestConfig = {}) => {
      return Get(`${this.identityServerUrl}/clients/captcha/${email}`, options)
    },
    getListOrganization: (
      options: AxiosRequestConfig = {},
    ): AxiosPromise<ListOrganizationResponse[] | AxiosError> =>
      Get(`${this.licenseManagerUrl}/organizations`, options),

    getLicensesById: (
      orgId: number,
      options: AxiosRequestConfig = {},
    ): AxiosPromise<ListOrganizationResponse[] | AxiosError> =>
      Get(`${this.licenseManagerUrl}/organizations/${orgId}/licenses`, options),

    getUsersByOrganization: (
      orgId: string,
      options: AxiosRequestConfig = {},
    ): AxiosPromise<UserListResponse | AxiosError> =>
      Get(`${this.licenseManagerUrl}/organizations/${orgId}/users`, options),
  }

  public Post = {
    registerUser: (data: RegisterUserData, options: AxiosRequestConfig = {}) =>
      Post(`${this.licenseManagerUrl}/users`, data, options),
    resetPasswordEmail: (email: string, options: AxiosRequestConfig = {}) =>
      Post(
        `${this.licenseManagerUrl}/users/${email}/notify/passwordreset`,
        {},
        options,
      ),
    login: (
      formValues: any,
      options: AxiosRequestConfig = {},
    ): AxiosPromise<AxiosResponse<LoginResponse> | AxiosError> => {
      const defaultData = {
        client_id: 'admin',
        grant_type: 'password',
        scope: 'API',
        ...formValues,
      }
      const data = qs.stringify(defaultData)
      const requestOptions = merge(
        {
          params: {
            ...defaultData,
          },
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Access-Control-Allow-Origin': '*',
            Accept: 'application/x-www-form-urlencoded',
          },
        },
        options,
      )
      return Post(
        `${this.identityServerUrl}/connect/token`,
        data,
        requestOptions,
      )
    },
    currentUserPreferences: (
      data: UserPreferencesData,
      options: AxiosRequestConfig = {},
    ) =>
      Post(
        `${this.licenseManagerUrl}/users/current/preferences`,
        data,
        options,
      ),
    createUser: (data: RegisterUserData) =>
      Post(`${this.licenseManagerUrl}/users/create`, data),

    createNewUser: (
      userId: number,
      input: NewLicenseData[],
      options: AxiosRequestConfig = {},
    ) => {
      Post(
        `${this.licenseManagerUrl}/organizations/users/${userId}/licenses`,
        input,
        options,
      )
    },
    createNewLicense: (
      orgId: string,
      input: NewLicenseData,
      options: AxiosRequestConfig = {},
    ) => {
      Post(
        `${this.licenseManagerUrl}/organizations/${orgId}/licenses`,
        input,
        options,
      )
    },
  }

  public Delete = {
    deleteUser: (
      userId: number,
      input: string[],
      options: AxiosRequestConfig = {},
    ) => {
      Delete(
        `${this.licenseManagerUrl}/organizations/users/${userId}/licenses`,
        input,
        options,
      )
    },
    deleteClaim: (productClaimId: string, options: AxiosRequestConfig = {}) => {
      Delete(`${this.licenseManagerUrl}/Claims/${productClaimId}`, options)
    },
  }
}

export const UserService = new UserServiceController()
